import React, { useState, useEffect } from 'react';
import { useQRGenerator, INITIAL_PAYLOADS } from '@/hooks/useQRGenerator';
import { usePWA } from '@/hooks/usePWA';
import { loadSettings, saveSettings } from '@/lib/storage/preferences';
import { AppSettings } from '@/types/settings';
import { TemplatePreset } from '@/constants/templates';
import { DecodedQRResult, QRType } from '@/types/qr';
import { downloadQRCode, copyQRImageToClipboard } from '@/lib/qr/export';

// Layout & UI Components
import { Header } from '@/components/layout/Header';
import { MobileBottomNav } from '@/components/layout/MobileBottomNav';
import { OfflineBanner } from '@/components/layout/OfflineBanner';
import { ToastProvider, useToast } from '@/components/ui/Toast';
import { CommandPalette } from '@/components/ui/CommandPalette';

// Feature Components
import { QRPreview } from '@/components/qr/QRPreview';
import { ContentTypeSelector } from '@/components/editor/ContentTypeSelector';
import { ContentEditor } from '@/components/editor/ContentEditor';
import { DesignEditor } from '@/components/editor/DesignEditor';
import { CameraScanner } from '@/components/scanner/CameraScanner';
import { ImageDropzoneScanner } from '@/components/scanner/ImageDropzoneScanner';
import { SecurityInspectorModal } from '@/components/scanner/SecurityInspectorModal';
import { BatchGenerator } from '@/components/batch/BatchGenerator';
import { HistoryManager } from '@/components/history/HistoryManager';
import { TemplatesGallery } from '@/components/templates/TemplatesGallery';
import { ToolsManager } from '@/components/tools/ToolsManager';
import { SettingsManager } from '@/components/tools/SettingsManager';

import {
  FileText,
  Palette,
  ScanLine,
  UploadCloud
} from 'lucide-react';

const AppContent: React.FC = () => {
  const { showToast } = useToast();
  const { isOnline, canInstallPWA, installApp } = usePWA();
  
  // Primary Navigation Tab
  const [activeTab, setActiveTab] = useState<string>('generator');
  
  // Editor sub-mode ('content' | 'design')
  const [editorMode, setEditorMode] = useState<'content' | 'design'>('content');

  // Scanner sub-mode ('camera' | 'image')
  const [scannerMode, setScannerMode] = useState<'camera' | 'image'>('camera');
  const [scannedResult, setScannedResult] = useState<DecodedQRResult | null>(null);
  const [isInspectorOpen, setIsInspectorOpen] = useState(false);

  // Command Palette
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);

  // App Settings
  const [settings, setSettings] = useState<AppSettings>(loadSettings);

  // Core QR Generator State
  const {
    selectedType,
    switchType,
    activePayload,
    updatePayload,
    design,
    setDesign,
    rawText,
    validationErrors,
    healthReport,
    isFavorite,
    toggleFavorite,
    loadItem
  } = useQRGenerator();

  // Global Keyboard Shortcuts (Cmd+K, Cmd+S)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsCommandPaletteOpen((prev) => !prev);
      } else if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        downloadQRCode(rawText, design, {
          format: settings.defaultExportFormat,
          size: settings.defaultExportSize,
          filename: 'qr-code-studio'
        });
        showToast('success', 'Download Started');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [rawText, design, settings, showToast]);

  const handleUpdateSettings = (newSettings: AppSettings) => {
    setSettings(newSettings);
    saveSettings(newSettings);
  };

  // True until the user edits a field — content still matches the built-in sample data
  const isSampleContent =
    JSON.stringify(activePayload) === JSON.stringify(INITIAL_PAYLOADS[selectedType]);

  const handleApplyTemplate = (template: TemplatePreset) => {
    setDesign(template.design);
    setActiveTab('generator');
  };

  const handleScanDecoded = (result: DecodedQRResult) => {
    setScannedResult(result);
    setIsInspectorOpen(true);
  };

  const handleOpenScannedInGenerator = (result: DecodedQRResult) => {
    if (result.parsedPayload && result.parsedPayload.type) {
      switchType(result.parsedPayload.type as QRType);
      updatePayload(result.parsedPayload as any);
    } else {
      switchType('text');
      updatePayload({ type: 'text', text: result.rawText });
    }
    setActiveTab('generator');
  };

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground pb-24 md:pb-12">
      {/* Offline Status Top Banner */}
      <OfflineBanner isOnline={isOnline} />

      {/* Primary Header */}
      <Header
        activeTab={activeTab}
        onSelectTab={setActiveTab}
        onOpenCommandPalette={() => setIsCommandPaletteOpen(true)}
        isOnline={isOnline}
        canInstallPWA={canInstallPWA}
        onInstallPWA={installApp}
      />

      {/* Main Workspace Area */}
      <main className="flex-1 max-w-[1440px] w-full mx-auto px-3.5 sm:px-6 lg:px-8 py-4 sm:py-8">
        
        {/* ===================== VIEW: GENERATOR ===================== */}
        {activeTab === 'generator' && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
            
            {/* Left Column: Content Type, Data Inputs & Design Panels */}
            <div className="lg:col-span-7 space-y-4 sm:space-y-6">
              
              {/* Content Type Selector Card */}
              <div className="p-4 sm:p-6 rounded-2xl bg-card border border-border shadow-xs">
                <ContentTypeSelector
                  selectedType={selectedType}
                  onSelectType={switchType}
                />
              </div>

              {/* Editor Workspace Card */}
              <div className="p-4 sm:p-6 rounded-2xl bg-card border border-border shadow-xs space-y-4 sm:space-y-5">
                {/* Content vs Design Toggle Bar */}
                <div className="flex items-center justify-between pb-3 sm:pb-4 border-b border-border">
                  <div className="flex items-center gap-1 p-1 bg-muted rounded-full w-full sm:w-auto justify-between sm:justify-start">
                    <button
                      onClick={() => setEditorMode('content')}
                      className={`flex-1 sm:flex-initial flex items-center justify-center gap-2 px-4 py-1.5 rounded-full text-[13px] font-normal tracking-tight transition-all cursor-pointer ${
                        editorMode === 'content'
                          ? 'bg-card text-primary font-semibold shadow-xs'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <FileText className="h-3.5 w-3.5" />
                      <span>Content</span>
                    </button>

                    <button
                      onClick={() => setEditorMode('design')}
                      className={`flex-1 sm:flex-initial flex items-center justify-center gap-2 px-4 py-1.5 rounded-full text-[13px] font-normal tracking-tight transition-all cursor-pointer ${
                        editorMode === 'design'
                          ? 'bg-card text-primary font-semibold shadow-xs'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Palette className="h-3.5 w-3.5" />
                      <span>Design & Style</span>
                    </button>
                  </div>

                  <span className="text-[12px] text-success hidden sm:inline font-medium">
                    ● Live Preview
                  </span>
                </div>

                {/* Sub-Editor Rendering */}
                {editorMode === 'content' ? (
                  <div className="space-y-4">
                    {isSampleContent && (
                      <div className="px-3.5 py-2.5 rounded-lg bg-warning/10 border border-warning/20 text-warning text-[13px] font-medium">
                        This is sample content — edit the fields below before sharing your QR code.
                      </div>
                    )}
                    <ContentEditor
                      payload={activePayload}
                      onChange={updatePayload}
                      errors={validationErrors}
                    />
                  </div>
                ) : (
                  <DesignEditor
                    design={design}
                    onChange={setDesign}
                  />
                )}
              </div>
            </div>

            {/* Right Column: Sticky Live QR Code Preview */}
            <div className="lg:col-span-5 lg:sticky lg:top-16 space-y-4 sm:space-y-6">
              <QRPreview
                rawText={rawText}
                design={design}
                health={healthReport}
                isFavorite={isFavorite}
                onToggleFavorite={toggleFavorite}
              />
            </div>
          </div>
        )}

        {/* ===================== VIEW: SCANNER ===================== */}
        {activeTab === 'scanner' && (
          <div className="max-w-2xl mx-auto space-y-4 sm:space-y-6">
            <div className="p-5 sm:p-6 rounded-2xl bg-card border border-border shadow-xs space-y-4 text-center">
              <h3 className="text-[24px] sm:text-[28px] font-semibold text-foreground tracking-[-0.28px] flex items-center justify-center gap-2">
                <ScanLine className="h-6 w-6 sm:h-7 sm:w-7 text-primary" />
                QR Scanner
              </h3>
              <p className="text-[13px] sm:text-[14px] text-muted-foreground max-w-md mx-auto">
                Scan using live camera or drag-and-drop an image file. Client-side security verification parses URLs safely.
              </p>

              {/* Mode Switcher */}
              <div className="inline-flex p-1 bg-muted rounded-full w-full sm:w-auto">
                <button
                  onClick={() => setScannerMode('camera')}
                  className={`flex-1 sm:flex-initial flex items-center justify-center gap-2 px-5 py-2 rounded-full text-[13px] font-normal tracking-tight transition-all cursor-pointer ${
                    scannerMode === 'camera'
                      ? 'bg-card text-primary font-semibold shadow-xs'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <ScanLine className="h-4 w-4" />
                  <span>Live Camera</span>
                </button>

                <button
                  onClick={() => setScannerMode('image')}
                  className={`flex-1 sm:flex-initial flex items-center justify-center gap-2 px-5 py-2 rounded-full text-[13px] font-normal tracking-tight transition-all cursor-pointer ${
                    scannerMode === 'image'
                      ? 'bg-card text-primary font-semibold shadow-xs'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <UploadCloud className="h-4 w-4" />
                  <span>Image File</span>
                </button>
              </div>
            </div>

            {/* Scanner Viewport */}
            {scannerMode === 'camera' ? (
              <CameraScanner
                isActive={activeTab === 'scanner' && scannerMode === 'camera'}
                onScanResult={handleScanDecoded}
              />
            ) : (
              <ImageDropzoneScanner onScanResult={handleScanDecoded} />
            )}
          </div>
        )}

        {/* ===================== VIEW: BATCH GENERATOR ===================== */}
        {activeTab === 'batch' && <BatchGenerator design={design} />}

        {/* ===================== VIEW: HISTORY ===================== */}
        {activeTab === 'history' && (
          <HistoryManager
            onLoadItem={(item) => {
              loadItem(item);
              setActiveTab('generator');
            }}
          />
        )}

        {/* ===================== VIEW: TEMPLATES ===================== */}
        {activeTab === 'templates' && (
          <TemplatesGallery
            currentRawText={rawText}
            onApplyTemplate={handleApplyTemplate}
          />
        )}

        {/* ===================== VIEW: TOOLS ===================== */}
        {activeTab === 'tools' && <ToolsManager />}

        {/* ===================== VIEW: SETTINGS ===================== */}
        {activeTab === 'settings' && (
          <SettingsManager
            settings={settings}
            onUpdateSettings={handleUpdateSettings}
            onInstallPWA={installApp}
            canInstallPWA={canInstallPWA}
          />
        )}
      </main>

      {/* Scanned Result Security Inspector Modal */}
      <SecurityInspectorModal
        result={scannedResult}
        isOpen={isInspectorOpen}
        onClose={() => setIsInspectorOpen(false)}
        onOpenInGenerator={handleOpenScannedInGenerator}
      />

      {/* Global Command Palette (⌘K) */}
      <CommandPalette
        isOpen={isCommandPaletteOpen}
        onClose={() => setIsCommandPaletteOpen(false)}
        onNavigate={setActiveTab}
        onSelectQRType={switchType}
        onDownloadPNG={() => {
          downloadQRCode(rawText, design, { format: 'png', size: 1024, filename: 'qr-code' });
        }}
        onDownloadSVG={() => {
          downloadQRCode(rawText, design, { format: 'svg', size: 1024, filename: 'qr-code' });
        }}
        onCopyQR={() => {
          copyQRImageToClipboard(rawText, design);
        }}
      />

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav activeTab={activeTab} onSelectTab={setActiveTab} />
    </div>
  );
};

export const App: React.FC = () => {
  return (
    <ToastProvider>
      <AppContent />
    </ToastProvider>
  );
};
